import { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

const PowerPointViewer = ({ contentId, directFileUrl, fileName }) => {
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [errorDetail, setErrorDetail] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!contentId) { setStatus('error'); return; }

    let revoke = null;
    const load = async () => {
      setStatus('loading');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE}/api/content/pptx-pdf/${contentId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `HTTP ${res.status}`);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        setPdfBlobUrl(url);
        setStatus('ready');
      } catch (err) {
        setErrorDetail(err.message || '');
        setStatus('error');
      }
    };

    load();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [contentId, retryCount]);

  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        await containerRef.current?.requestFullscreen?.();
        setIsFullscreen(true);
      } else {
        if (document.fullscreenElement) await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch {}
  };

  useEffect(() => {
    const onFsc = () => { if (!document.fullscreenElement) setIsFullscreen(false); };
    document.addEventListener('fullscreenchange', onFsc);
    return () => document.removeEventListener('fullscreenchange', onFsc);
  }, []);

  return (
    <div
      ref={containerRef}
      className={isFullscreen ? 'fixed inset-0 z-50 bg-black flex flex-col' : 'bg-gray-50 rounded-2xl border-2 border-gray-200 flex flex-col'}
      style={isFullscreen ? {} : { height: '680px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between bg-white border-b border-gray-200 px-4 py-3 flex-shrink-0">
        <div>
          <p className="font-bold text-gray-800 truncate">{fileName}</p>
          <p className="text-xs text-gray-500">View-Only Mode • No Downloads Allowed</p>
        </div>
        <div className="flex items-center gap-2">
          {status === 'ready' && (
            <button
              onClick={toggleFullscreen}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
            >
              {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            </button>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 relative overflow-hidden">
        {status === 'loading' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mb-4" />
            <p className="text-gray-700 font-semibold">Converting presentation…</p>
            <p className="text-gray-400 text-sm mt-1">PowerPoint is generating the PDF preview</p>
          </div>
        )}

        {status === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white p-6 text-center">
            <div className="text-5xl mb-3">📊</div>
            <p className="text-lg font-bold text-gray-700 mb-1">Auto-preview failed</p>
            <p className="text-gray-400 text-xs mb-5 max-w-xs">
              {errorDetail || 'Office COM conversion unavailable'}
            </p>
            <div className="flex flex-col gap-3 w-full max-w-xs">
              {directFileUrl && (
                <a
                  href={directFileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-xl text-sm transition-colors"
                >
                  Open Presentation in Browser
                </a>
              )}
              <button
                onClick={() => { setPdfBlobUrl(null); setErrorDetail(''); setRetryCount(c => c + 1); }}
                className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-2.5 rounded-xl text-sm transition-colors"
              >
                Retry Conversion
              </button>
            </div>
          </div>
        )}

        {status === 'ready' && pdfBlobUrl && (
          <iframe
            src={`${pdfBlobUrl}#toolbar=0&navpanes=0&view=FitH`}
            title={fileName}
            className="w-full h-full"
            style={{ border: 'none', display: 'block' }}
          />
        )}
      </div>
    </div>
  );
};

export default PowerPointViewer;
